import { useMemo } from 'react';
import { usePostHog } from 'posthog-js/react';
import { PostHogInstance } from 'shared/posthogCapture/types';
import { EmailProvider } from 'shared/components/EmailComposeMenu/_index';

type PresetKey = 'today' | 'yesterday' | 'last7' | 'last30' | 'last90';

interface DateRangePresetSelectedProperties {
  preset: PresetKey;
}

interface SiteFilterChangedProperties {
  selection_type: 'all' | 'specific';
  site_count: number;
}

interface OverviewViewSelectedProperties {
  view: 'numbers' | 'urgency' | 'risks';
}

interface EventEmailSentProperties {
  provider: EmailProvider;
}

export const createDashboardCaptureEvents = (posthog: PostHogInstance) => ({
  dateRangeCustomApplied: () => {
    posthog?.capture('dashboard / filters / date range / custom applied');
  },
  dateRangePresetSelected: (properties: DateRangePresetSelectedProperties) => {
    posthog?.capture(
      'dashboard / filters / date range / preset selected',
      properties
    );
  },
  eventEmailSent: (properties: EventEmailSentProperties) => {
    posthog?.capture('dashboard / event / email sent', properties);
  },
  eventOpened: () => {
    posthog?.capture('dashboard / event / opened');
  },
  eventsLoadedMore: () => {
    posthog?.capture('dashboard / events / loaded more');
  },
  overviewViewSelected: (properties: OverviewViewSelectedProperties) => {
    posthog?.capture('dashboard / overview / view selected', properties);
  },
  shiftSummariesClicked: () => {
    posthog?.capture('dashboard / shift summaries / clicked');
  },
  siteFilterChanged: (properties: SiteFilterChangedProperties) => {
    posthog?.capture('dashboard / filters / site / changed', properties);
  },
});

export type DashboardCaptureEvents = ReturnType<
  typeof createDashboardCaptureEvents
>;

export const usePosthogCapture = () => {
  const posthog = usePostHog();
  return useMemo(() => createDashboardCaptureEvents(posthog), [posthog]);
};
